import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../lib/auth";
import { paymentsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
    Shield, 
    CheckCircle2,
    ArrowRight,
    Sparkles,
    FileText,
    Clock
} from "lucide-react";
import { toast } from "sonner";

const PaymentSuccessPage = () => {
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('checking');
    const [pollCount, setPollCount] = useState(0);
    const [paymentMode, setPaymentMode] = useState(null);

    const sessionId = searchParams.get('session_id');
    const planFromUrl = searchParams.get('plan');
    const serviceFromUrl = searchParams.get('service');
    const pollCountRef = useRef(0);

    const pollPaymentStatus = useCallback(async () => {
        const maxAttempts = 5;
        const pollInterval = 2000;

        if (pollCountRef.current >= maxAttempts) {
            setStatus('timeout');
            return;
        }

        try {
            const response = await paymentsAPI.getStatus(sessionId);

            if (response.data.payment_status === 'paid') {
                setStatus('success');
                const detectedMode = response.data.service === 'mail'
                    ? 'mail'
                    : (response.data.mode || planFromUrl || 'one_time');
                setPaymentMode(detectedMode);
                if (detectedMode !== 'mail') {
                    await refreshUser();
                }
                if (detectedMode === 'mail') {
                    toast.success("Payment received — your letter is being mailed via USPS certified.");
                } else if (response.data.mode === 'subscription') {
                    toast.success("Subscription active! Full access unlocked.");
                } else {
                    toast.success("Payment successful! Full access unlocked.");
                }
                return;
            } else if (response.data.status === 'expired') {
                setStatus('expired');
                return;
            }

            // Continue polling
            pollCountRef.current += 1;
            setPollCount(pollCountRef.current);
            setTimeout(pollPaymentStatus, pollInterval);
        } catch (error) {
            toast.error("We couldn't confirm your payment. Please refresh the page in a moment.");
            setStatus('error');
        }
    }, [sessionId, planFromUrl, refreshUser]);

    useEffect(() => {
        if (!sessionId) {
            navigate('/dashboard');
            return;
        }
        pollPaymentStatus();
    }, [sessionId, navigate, pollPaymentStatus]);

    const getStatusContent = () => {
        switch (status) {
            case 'checking':
                return {
                    icon: <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mx-auto" />,
                    title: "Confirming Your Payment",
                    description: "Please wait while we verify your payment..."
                };
            case 'success':
                if (paymentMode === 'mail') {
                    return {
                        icon: <CheckCircle2 className="h-20 w-20 text-accent mx-auto" />,
                        title: "Letter on its way!",
                        description: "We've handed your dispute letter off for USPS certified mail. You'll get a text with the tracking number once USPS picks it up.",
                    };
                }
                return {
                    icon: <CheckCircle2 className="h-20 w-20 text-accent mx-auto" />,
                    title: paymentMode === 'subscription' || paymentMode === 'monthly'
                        ? "Subscription Active!"
                        : "Payment Successful!",
                    description: paymentMode === 'subscription' || paymentMode === 'monthly'
                        ? "Your monthly plan is live. Cancel anytime from your dashboard."
                        : "You now have full access to all CreditFix AI features."
                };
            case 'timeout':
                return {
                    icon: <Clock className="h-20 w-20 text-muted-foreground mx-auto" />,
                    title: "Taking Longer Than Expected",
                    description: "Your payment may still be processing. Please check your email for confirmation."
                };
            case 'expired':
                return {
                    icon: <Clock className="h-20 w-20 text-destructive mx-auto" />,
                    title: "Session Expired",
                    description: "Your payment session has expired. Please try again."
                };
            case 'error':
                return {
                    icon: <Clock className="h-20 w-20 text-destructive mx-auto" />,
                    title: "Something Went Wrong",
                    description: "We couldn't verify your payment. Please contact support if you were charged."
                };
            default:
                return {
                    icon: <Sparkles className="h-20 w-20 text-primary mx-auto" />,
                    title: "Processing...",
                    description: "Please wait..."
                };
        }
    };

    const content = getStatusContent();

    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="nav-glass fixed top-0 left-0 right-0 z-50">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Shield className="h-8 w-8 text-primary" />
                        <span className="font-serif text-xl font-semibold">CreditFix AI</span>
                    </Link>
                </div>
            </nav>

            <main className="pt-24 px-4 md:px-8 flex items-center justify-center min-h-screen">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-lg w-full"
                >
                    <Card className="border-border shadow-soft">
                        <CardContent className="pt-12 pb-8 text-center">
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.2, type: "spring" }}
                            >
                                {content.icon}
                            </motion.div>
                            
                            <h1 className="text-2xl font-serif font-bold text-foreground mt-6 mb-2">
                                {content.title}
                            </h1>
                            <p className="text-muted-foreground mb-8">
                                {content.description}
                            </p>

                            {status === 'success' && (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className="space-y-4"
                                >
                                    <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
                                        <h3 className="font-medium text-foreground mb-2">What's Included:</h3>
                                        <ul className="text-sm text-muted-foreground space-y-2 text-left">
                                            <li className="flex items-center gap-2">
                                                <CheckCircle2 className="h-4 w-4 text-accent" />
                                                Complete dispute letters ready to send
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <CheckCircle2 className="h-4 w-4 text-accent" />
                                                Full FCRA & CFPB legal citations
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <CheckCircle2 className="h-4 w-4 text-accent" />
                                                Detailed action plan with timelines
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <CheckCircle2 className="h-4 w-4 text-accent" />
                                                Print and download capabilities
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <Link to="/letters" className="flex-1">
                                            <Button className="w-full rounded-full btn-press" data-testid="view-letters-btn">
                                                <FileText className="h-4 w-4 mr-2" />
                                                View My Letters
                                            </Button>
                                        </Link>
                                        <Link to="/dashboard" className="flex-1">
                                            <Button variant="outline" className="w-full rounded-full" data-testid="go-dashboard-btn">
                                                Go to Dashboard
                                                <ArrowRight className="h-4 w-4 ml-2" />
                                            </Button>
                                        </Link>
                                    </div>
                                </motion.div>
                            )}

                            {(status === 'timeout' || status === 'error') && (
                                <div className="flex flex-col gap-3">
                                    <Link to="/dashboard">
                                        <Button className="w-full rounded-full btn-press">
                                            Return to Dashboard
                                        </Button>
                                    </Link>
                                    <Button 
                                        variant="outline" 
                                        className="w-full rounded-full"
                                        onClick={() => {
                                            setStatus('checking');
                                            setPollCount(0);
                                            pollPaymentStatus();
                                        }}
                                    >
                                        Check Again
                                    </Button>
                                </div>
                            )}

                            {status === 'expired' && (
                                <Link to="/dashboard">
                                    <Button className="w-full rounded-full btn-press">
                                        Return to Dashboard
                                    </Button>
                                </Link>
                            )}
                        </CardContent>
                    </Card>
                </motion.div>
            </main>
        </div>
    );
};

export default PaymentSuccessPage;
